import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Snackbar,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Star as StarIcon,
  CalendarToday as CalendarTodayIcon,
  AttachFile as AttachFileIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS, FK_NAMES, getCreditTypes, getModules, getPredefinedActivities } from '../lib/tables';
import { useAuth } from '../context/AuthContext';
import { auditActions } from '../lib/auditLog';

export default function ReviewRequests() {
  const { profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvedCredits, setApprovedCredits] = useState(1);
  const [stats, setStats] = useState({ total: 0, pending: 0, validated: 0, rejected: 0 });
  const [filterModule, setFilterModule] = useState('all');
  const [modules, setModules] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const loadDataAsync = async () => {
      if (profile && isMounted) {
        await loadData(isMounted);
      }
    };
    
    loadDataAsync();
    return () => { isMounted = false; };
  }, [profile]);

  const loadData = async (isMounted = true) => {
    if (!profile) return;

    

    setLoading(true);
    try {
      
      const { data: modulesData } = await supabase
        .from(TABLES.modules)
        .select('*')
        .order('ordine');
      
      if (!isMounted) return;
      setModules(modulesData || []);

      
      
      if (profile.rol === 'profesor') {
        
        
        const { data: validTypes, error: typesError } = await supabase
          .from(TABLES.credit_types)
          .select('id')
          .or('validator_rol.eq.profesor,validator_rol.eq.coordonator');
        
        if (typesError) throw typesError;
        
        const validTypeIds = validTypes?.map(t => t.id) || [];
        
        
        
        if (validTypeIds.length === 0) {
          setRequests([]);
          setFilteredRequests([]);
          setStats({ total: 0, pending: 0, validated: 0, rejected: 0 });
          setLoading(false);
          return;
        }

        
        const { data: requestsData, error: requestsError } = await supabase
          .from(TABLES.credit_requests)
          .select(`
            *,
            ${RELATIONS.credit_types} (*),
            ${RELATIONS.modules} (*),
            profiles_elev:${RELATIONS.profiles}!${FK_NAMES.credit_requests_id_elev}(*),
            ${RELATIONS.predefined_activities} (*)
          `)
          .eq('status', 'in_asteptare')
          .in('id_tip_credit', validTypeIds)
          .order('data_cerere', { ascending: false });

        if (requestsError) throw requestsError;
        
        
        const filteredForProfessor = requestsData?.filter(req => 
          getCreditTypes(req)?.validator_rol === 'profesor' || 
          getCreditTypes(req)?.validator_rol === 'coordonator'
        ) || [];
        
        
        setRequests(filteredForProfessor);
        setFilteredRequests(filteredForProfessor);
        setStats({
          total: filteredForProfessor.length,
          pending: filteredForProfessor.length,
          validated: 0,
          rejected: 0,
        });
        
      } else if (profile.rol === 'coordonator') {
        
        const { data: requestsData, error: requestsError } = await supabase
          .from(TABLES.credit_requests)
          .select(`
            *,
            ${RELATIONS.credit_types} (*),
            ${RELATIONS.modules} (*),
            profiles_elev:${RELATIONS.profiles}!${FK_NAMES.credit_requests_id_elev}(*),
            ${RELATIONS.predefined_activities} (*)
          `)
          .eq('status', 'in_asteptare')
          .order('data_cerere', { ascending: false });

        if (requestsError) throw requestsError;
        
        
        setRequests(requestsData || []);
        setFilteredRequests(requestsData || []);
        setStats({
          total: requestsData?.length || 0,
          pending: requestsData?.length || 0,
          validated: 0,
          rejected: 0,
        });
        
      } else {
        
        setRequests([]);
        setFilteredRequests([]);
        setStats({ total: 0, pending: 0, validated: 0, rejected: 0 });
      }

    } catch (error) {
      console.error('Eroare detaliată la încărcarea cererilor:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      if (isMounted) showSnackbar('Eroare la încărcarea cererilor: ' + error.message, 'error');
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  
  const calculateCreditsForRequest = (request) => {
    if (!request) return 1;

    
    if (request.predefined_activities && request.predefined_activities.credite_per_ora) {
      const hours = request.ore_realizate || 0;
      const creditsPerHour = request.predefined_activities.credite_per_ora;
      const calculated = hours * creditsPerHour;
      return Math.max(1, Math.ceil(calculated));
    }

    
    
    let creditsPerHour = 1.00;
    if (getCreditTypes(request)?.nume_tip?.toLowerCase() === 'curricular') {
      creditsPerHour = 0.10;
    }
    
    const hours = request.ore_realizate || 0;
    const calculated = hours * creditsPerHour;
    return Math.max(1, Math.ceil(calculated));
  };

  const formatCreditsPerHour = (request) => {
    if (!request) return "1 credit / oră";
    
    
    const predefinedAct = getPredefinedActivities(request);
    if (predefinedAct && predefinedAct.credite_per_ora) {
      const creditsPerHour = predefinedAct.credite_per_ora;
      if (creditsPerHour === 0.10) return "1 credit / 10 ore";
      if (creditsPerHour === 1.00) return "1 credit / oră";
      if (creditsPerHour === 2.00) return "2 credite / oră";
      if (creditsPerHour === 1.50) return "1.5 credite / oră";
      return `${creditsPerHour} credite / oră`;
    }
    
    
    if (getCreditTypes(request)?.nume_tip?.toLowerCase() === 'curricular') {
      return "1 credit / 10 ore";
    }
    
    return "1 credit / oră";
  };

  const handleApproveRequest = async (request, approvedCredits) => {
    try {
      

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nu ești autentificat!');

      
      const { error: updateRequestError } = await supabase
        .from(TABLES.credit_requests)
        .update({
          status: 'validat',
          data_validare: new Date().toISOString(),
          id_validator: user.id,
          credite_acordate: approvedCredits,
        })
        .eq('id', request.id);

      if (updateRequestError) throw updateRequestError;

      

      
      const { data: existingCredit } = await supabase
        .from(TABLES.credits_obtained)
        .select('id, credite_acumulate')
        .eq('id_elev', request.id_elev)
        .eq('id_module', request.id_module)
        .maybeSingle();

      if (existingCredit) {
        await supabase
          .from(TABLES.credits_obtained)
          .update({ 
            credite_acumulate: existingCredit.credite_acumulate + approvedCredits,
            data_ultima_actualizare: new Date().toISOString()
          })
          .eq('id', existingCredit.id);
        
      } else {
        await supabase
          .from(TABLES.credits_obtained)
          .insert({
            id_elev: request.id_elev,
            id_module: request.id_module,
            credite_acumulate: approvedCredits,
            data_ultima_actualizare: new Date().toISOString(),
          });
        
      }

      
      await auditActions.approveRequest(request, approvedCredits);
      
      await loadData();
      setActionDialogOpen(false);
      setDialogOpen(false);
      
      showSnackbar(`Cererea #${request.id} aprobată!`, 'success');

    } catch (error) {
      console.error('Eroare:', error);
      showSnackbar(`${error.message}`, 'error');
    }
  };

  const handleRejectRequest = async (request, reason) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nu ești autentificat!');

      const { error } = await supabase
        .from(TABLES.credit_requests)
        .update({
          status: 'respins',
          data_validare: new Date().toISOString(),
          id_validator: user.id,
          motiv_respingere: reason,
        })
        .eq('id', request.id);

      if (error) throw error;

      
      await supabase
        .from(TABLES.notifications)
        .insert({
          id_user: request.id_elev,
          titlu: 'Cerere respinsă',
          mesaj: `Cererea ta "${request.nume_activitate}" a fost respinsă: ${reason}`,
          tip: 'error',
          citita: false,
        });

      
      await auditActions.rejectRequest(request, reason);

      await loadData();
      setActionDialogOpen(false);
      setDialogOpen(false);

      showSnackbar('Cererea a fost respinsă', 'info');

    } catch (error) {
      console.error('Eroare la respingere:', error);
      showSnackbar(`Eroare: ${error.message}`, 'error');
    }
  };

  
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

  const handleSelectAll = () => {
    const pendingRequests = filteredRequests.filter(r => r.status === 'in_asteptare');
    if (selectedRequests.length === pendingRequests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(pendingRequests.map(r => r.id));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;
    
    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nu ești autentificat!');

      for (const requestId of selectedRequests) {
        const request = requests.find(r => r.id === requestId);
        if (!request || request.status !== 'in_asteptare') continue;

        try {
          const estimatedCredits = calculateCreditsForRequest(request);
          
          const { error: updateError } = await supabase
            .from(TABLES.credit_requests)
            .update({
              status: 'validat',
              data_validare: new Date().toISOString(),
              id_validator: user.id,
              credite_acordate: estimatedCredits,
            })
            .eq('id', request.id);

          if (updateError) throw updateError;

          
          await supabase
            .from(TABLES.notifications)
            .insert({
              id_user: request.id_elev,
              titlu: 'Cerere aprobată',
              mesaj: `Cererea ta "${request.nume_activitate}" a fost aprobată cu ${estimatedCredits} credite!`,
              tip: 'success',
              citita: false,
            });

          successCount++;
        } catch (err) {
          console.error(`Error approving request ${requestId}:`, err);
          errorCount++;
        }
      }

      await loadData();
      setSelectedRequests([]);
      setBulkDialogOpen(false);
      
      
      if (successCount > 0) {
        await auditActions.bulkApprove(successCount, selectedRequests);
      }
      
      if (errorCount === 0) {
        showSnackbar(`${successCount} cereri aprobate cu succes!`, 'success');
      } else {
        showSnackbar(`${successCount} aprobate, ${errorCount} erori`, 'warning');
      }
    } catch (error) {
      showSnackbar(`Eroare: ${error.message}`, 'error');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    const estimatedCredits = calculateCreditsForRequest(request);
    setApprovedCredits(estimatedCredits);
    setDialogOpen(true);
  };

  const handleOpenActionDialog = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setRejectionReason('');
    
    if (type === 'approve') {
      const estimatedCredits = calculateCreditsForRequest(request);
      setApprovedCredits(estimatedCredits);
    }
    
    setActionDialogOpen(true);
  };

  const handleFilterChange = (event) => {
    const moduleId = event.target.value;
    setFilterModule(moduleId);
    
    if (moduleId === 'all') {
      setFilteredRequests(requests);
    } else {
      const filtered = requests.filter(req => req.id_module === moduleId);
      setFilteredRequests(filtered);
    }
  };

  const getStatusChip = (status) => {
    const config = {
      'in_asteptare': { label: 'În așteptare', color: 'warning', icon: <HourglassEmptyIcon /> },
      'validat': { label: 'Validat', color: 'success', icon: <CheckCircleIcon /> },
      'respins': { label: 'Respins', color: 'error', icon: <CancelIcon /> },
    };
    
    const { label, color, icon } = config[status] || { label: status, color: 'default' };
    
    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 'medium' }}
      />
    );
  };

  const getTypeChip = (type) => {
    return (
      <Chip
        label={type?.nume_tip}
        size="small"
        sx={{
          backgroundColor: `${type?.culoare}20`,
          color: type?.culoare,
          borderColor: type?.culoare,
        }}
        variant="outlined"
      />
    );
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Validare Cereri
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {profile?.rol === 'profesor' 
            ? 'Validează credite curriculare și transferabile'
            : 'Validează toate tipurile de credite (curricular, extracurricular, transferabil)'}
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Card sx={{ borderRadius: 3, height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
          <CardContent sx={{ textAlign: 'center', p: 0 }}>
            <Typography variant="h3" color="primary" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              Cereri În Așteptare
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filtrează după modul</InputLabel>
            <Select
              value={filterModule}
              onChange={handleFilterChange}
              label="Filtrează după modul"
            >
              <MenuItem value="all">Toate modulele</MenuItem>
              {modules.map((module) => (
                <MenuItem key={module.id} value={module.id}>
                  {module.cod_module}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Bulk approve button */}
          {selectedRequests.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DoneAllIcon />}
              onClick={() => setBulkDialogOpen(true)}
              sx={{ ml: 2 }}
            >
              Aprobă {selectedRequests.length} cereri
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {filteredRequests.filter(r => r.status === 'in_asteptare').length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedRequests.length} / {filteredRequests.filter(r => r.status === 'in_asteptare').length} selectate
            </Typography>
          )}
          <Tooltip title="Reîncarcă">
            <IconButton onClick={loadData} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabel cereri */}
      {filteredRequests.length === 0 ? (
        <Card sx={{ py: 8, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nicio cerere în așteptare pentru validare
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Toate cererile au fost procesate
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRequests.length > 0 && selectedRequests.length < filteredRequests.filter(r => r.status === 'in_asteptare').length}
                    checked={filteredRequests.filter(r => r.status === 'in_asteptare').length > 0 && selectedRequests.length === filteredRequests.filter(r => r.status === 'in_asteptare').length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Elev</TableCell>
                <TableCell>Activitate</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Modul</TableCell>
                <TableCell>Ore realizate</TableCell>
                <TableCell>Data depunerii</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow 
                  key={request.id} 
                  hover
                  selected={selectedRequests.includes(request.id)}
                >
                  <TableCell padding="checkbox">
                    {request.status === 'in_asteptare' && (
                      <Checkbox
                        checked={selectedRequests.includes(request.id)}
                        onChange={() => handleSelectRequest(request.id)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                        {request.profiles_elev?.prenume?.[0] || 'E'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {request.profiles_elev?.prenume} {request.profiles_elev?.nume}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.profiles_elev?.clasa}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {request.nume_activitate}
                    </Typography>
                    {request.descriere && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {request.descriere.substring(0, 50)}...
                      </Typography>
                    )}
                    {request.predefined_activities && (
                      <Chip
                        label="Activitate predefinită"
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{getTypeChip(getCreditTypes(request))}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getModules(request)?.cod_module}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {request.ore_realizate ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          {request.ore_realizate} ore
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({calculateCreditsForRequest(request)} credite estim.)
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Nespecificat
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Vezi detalii">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(request)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Aprobă">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleOpenActionDialog(request, 'approve')}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Respinge">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenActionDialog(request, 'reject')}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog pentru detalii */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        {selectedRequest && (
          <>
            <DialogTitle sx={{ 
              p: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'white', 
                      color: '#667eea',
                      width: 56,
                      height: 56
                    }}>
                      {selectedRequest.profiles_elev?.prenume?.[0] || 'E'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {selectedRequest.profiles_elev?.prenume} {selectedRequest.profiles_elev?.nume}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        <SchoolIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {selectedRequest.profiles_elev?.clasa} • {selectedRequest.profiles_elev?.specializare}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label={`#${selectedRequest.id}`}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Detalii activitate
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                            {selectedRequest.nume_activitate}
                          </Typography>
                          
                          {selectedRequest.descriere && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              {selectedRequest.descriere}
                            </Typography>
                          )}
                          
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Tip activitate
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                {getTypeChip(getCreditTypes(selectedRequest))}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Validator: {getCreditTypes(selectedRequest)?.validator_rol}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Modul
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {getModules(selectedRequest)?.cod_module} - {getModules(selectedRequest)?.nume_module}
                              </Typography>
                            </Grid>
                            
                            {getPredefinedActivities(selectedRequest) && (
                              <Grid item xs={12}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  mt: 1
                                }}>
                                  <InfoIcon color="info" fontSize="small" />
                                  <Typography variant="caption" color="text.secondary">
                                    Activitate predefinită • {getPredefinedActivities(selectedRequest)?.descriere}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ 
                      height: '100%',
                      borderColor: getPredefinedActivities(selectedRequest)?.credite_per_ora > 0.10 ? 'success.main' : 'divider'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Calcul credite
                        </Typography>
                        
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ 
                            bgcolor: '#f8f9fa', 
                            p: 2, 
                            borderRadius: 2,
                            mb: 2
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Formula
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                              {selectedRequest.ore_realizate} ore × {formatCreditsPerHour(selectedRequest)}
                            </Typography>
                          </Box>

                          <Typography variant="caption" color="text.secondary">
                            Credite estimate
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="success.main" gutterBottom>
                            {calculateCreditsForRequest(selectedRequest)}
                          </Typography>
                          
                          {(getPredefinedActivities(selectedRequest)?.credite_per_ora > 0.10 || 
                            getCreditTypes(selectedRequest)?.nume_tip?.toLowerCase() !== 'curricular') && (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: 1,
                              mt: 2
                            }}>
                              <StarIcon color="warning" fontSize="small" />
                              <Typography variant="caption" color="success.main" fontWeight="bold">
                                Activitate cu valoare ridicată
                              </Typography>
                            </Box>
                          )}
                          
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                            {formatCreditsPerHour(selectedRequest)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <AccessTimeIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {selectedRequest.ore_realizate || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ore realizate
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        {getStatusChip(selectedRequest.status)}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Status cerere
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <CalendarTodayIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="body1" fontWeight="bold">
                          {new Date(selectedRequest.data_cerere).toLocaleDateString('ro-RO')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Data depunerii
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <PersonIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {getCreditTypes(selectedRequest)?.validator_rol || 'Nespecificat'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Validator necesar
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {selectedRequest.document_url && (
                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: '#e3f2fd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <AttachFileIcon color="primary" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Document atașat
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Confirmare activitate
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          href={selectedRequest.document_url}
                          target="_blank"
                          size="small"
                        >
                          Deschide
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Card variant="outlined" sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Contact elev
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {selectedRequest.profiles_elev?.email}
                          </Typography>
                        </Box>
                      </Grid>
                      {selectedRequest.profiles_elev?.telefon && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {selectedRequest.profiles_elev?.telefon}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </DialogContent>

            <DialogActions sx={{ 
              p: 3, 
              borderTop: 1, 
              borderColor: 'divider',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Button 
                  onClick={() => setDialogOpen(false)}
                  variant="outlined"
                  startIcon={<CloseIcon />}
                >
                  Închide
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => handleOpenActionDialog(selectedRequest, 'reject')}
                  sx={{ minWidth: 120 }}
                >
                  Respinge
                </Button>
                <Button
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleOpenActionDialog(selectedRequest, 'approve')}
                  sx={{ 
                    minWidth: 120,
                    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
                  }}
                >
                  Aprobă
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog pentru aprobare/respingere */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">
                Aprobă Cerere
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon color="error" />
              <Typography variant="h6">
                Respinge Cerere
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {actionType === 'approve' 
              ? `Ești sigur că vrei să aprobi cererea "${selectedRequest?.nume_activitate}"?`
              : `Ești sigur că vrei să respingi cererea "${selectedRequest?.nume_activitate}"?`
            }
          </Typography>
          
          {actionType === 'approve' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Configurare credite
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ore realizate
                    </Typography>
                    <Typography variant="h6">
                      {selectedRequest?.ore_realizate || 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Credite calculate
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {calculateCreditsForRequest(selectedRequest)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCreditsPerHour(selectedRequest)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Credite acordate (poți ajusta)
                </Typography>
                <TextField
                  type="number"
                  value={approvedCredits}
                  onChange={(e) => setApprovedCredits(Math.max(1, parseInt(e.target.value) || 1))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">credite</InputAdornment>,
                  }}
                  helperText="Poți ajusta numărul de credite acordate"
                />
              </FormControl>
            </Box>
          )}
          
          {actionType === 'reject' && (
            <TextField
              autoFocus
              margin="dense"
              label="Motiv respingere"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
              helperText="Specifică motivul respingerii (obligatoriu)"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setActionDialogOpen(false)}>
            Anulează
          </Button>
          <Button
            onClick={() => {
              if (actionType === 'approve') {
                handleApproveRequest(selectedRequest, approvedCredits);
              } else {
                handleRejectRequest(selectedRequest, rejectionReason);
              }
            }}
            color={actionType === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={actionType === 'reject' && !rejectionReason.trim()}
          >
            {actionType === 'approve' ? `Aprobă (${approvedCredits} credite)` : 'Respinge cererea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmare bulk approve */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkProcessing && setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DoneAllIcon color="success" />
            Confirmare aprobare în masă
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Vei aproba <strong>{selectedRequests.length}</strong> cereri cu creditele estimate automat.
          </Alert>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cereri selectate:
          </Typography>
          
          <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
            {selectedRequests.map(requestId => {
              const request = requests.find(r => r.id === requestId);
              if (!request) return null;
              const estimatedCredits = calculateCreditsForRequest(request);
              return (
                <Box 
                  key={requestId} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    py: 0.5, 
                    borderBottom: '1px solid #eee' 
                  }}
                >
                  <Typography variant="body2">
                    {request.profiles_elev?.prenume} {request.profiles_elev?.nume} - {request.nume_activitate}
                  </Typography>
                  <Chip label={`${estimatedCredits} cr`} size="small" color="success" />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDialogOpen(false)} 
            disabled={bulkProcessing}
          >
            Anulează
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApprove}
            disabled={bulkProcessing}
            startIcon={bulkProcessing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {bulkProcessing ? 'Se procesează...' : `Aprobă toate (${selectedRequests.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pentru notificări */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}